import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { HiOutlineX } from "react-icons/hi";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/TextLayer.css";
import "react-pdf/dist/Page/AnnotationLayer.css";

import DateTimePicker from "../../components/DateTimePicker";
import pdfIcon from "../../assets/svg/pdf-icon.svg";
import {
  generateLegalCertificateCoveringNotePdf,
  getLegalCertificateCoveringNote,
  LEGAL_CERTIFICATE_TYPE_OF_MARK_OPTIONS,
  saveLegalCertificateCoveringNote,
  type LegalCertificateCoveringNoteApplicationItem,
  type LegalCertificateCoveringNoteContext,
} from "../../api/legalCertificate";
import { downloadFileFromUrlWithFallback } from "../../utils/downloadFileFromUrl";
import { parseFilingApiDateString, toLocalYmd } from "../../utils/filingApiDates";

pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

export const LEGAL_CERTIFICATE_PREPARE_KIT_FORM_ID =
  "legal-certificate-prepare-kit-form";

const LABEL_STYLE = "text-xs font-normal leading-5 flex items-center";
const LABEL_FEATURES = { fontFeatureSettings: "'ss01' on, 'cv01' on" } as const;

type KitItemKey =
  | "coveringNote"
  | "originalLegalCertificate"
  | "registrationCertificate"
  | "framedCertificate";

const KIT_ITEMS: { key: KitItemKey; label: string }[] = [
  { key: "coveringNote", label: "Covering Note" },
  { key: "originalLegalCertificate", label: "Original copy of Legal Certificate" },
  {
    key: "registrationCertificate",
    label: "Colour Printout of Registration Certificate",
  },
  { key: "framedCertificate", label: "Framed Certificate" },
];

function formatReadOnlyDate(value: string | null): string {
  const d = parseFilingApiDateString(value);
  if (!d) return "";
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${dd}-${mm}-${yyyy}`;
}

function formatTypeOfMarkLabel(value: string | null): string {
  if (!value) return "";
  const lower = value.toLowerCase();
  const match = LEGAL_CERTIFICATE_TYPE_OF_MARK_OPTIONS.find(
    (o) => o.toLowerCase() === lower
  );
  return match ?? value;
}

function CheckboxBox({ checked }: { checked: boolean }) {
  return (
    <span
      className="flex items-center justify-center w-6 h-6 flex-shrink-0"
      aria-hidden
    >
      <span
        className={`flex items-center justify-center w-5 h-5 rounded-md border ${
          checked
            ? "bg-[#222222] border-[#222222]"
            : "bg-white border-[#D6D8DB]"
        }`}
      >
        {checked && (
          <svg
            width="12"
            height="12"
            viewBox="0 0 12 12"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M2.5 6.2 L4.8 8.5 L9.5 3.3"
              stroke="white"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              fill="none"
            />
          </svg>
        )}
      </span>
    </span>
  );
}

function FetchedDetailsPanel({
  rows,
}: {
  rows: Array<{ label: string; value: string }>;
}) {
  return (
    <div
      className="bg-[#F7F7F7] rounded-[8px] p-4 md:p-5 flex flex-col gap-3 w-full"
      style={{
        fontFamily: "Inter, sans-serif",
        fontFeatureSettings: "'ss01' on, 'cv01' on",
      }}
    >
      {rows.map(({ label, value }) => (
        <div
          key={label}
          className="flex flex-col md:flex-row md:items-baseline gap-1 md:gap-4"
        >
          <span className="text-xs font-normal leading-5 text-[#6D6D6D] md:w-[220px] md:shrink-0">
            {label}
          </span>
          <span
            className={`text-xs font-normal leading-5 break-words ${
              value ? "text-[#222222]" : "text-[#6D6D6D]"
            }`}
          >
            {value || "—"}
          </span>
        </div>
      ))}
    </div>
  );
}

function ApplicationsTable({
  rows,
}: {
  rows: LegalCertificateCoveringNoteApplicationItem[];
}) {
  return (
    <div
      className="bg-[#F7F7F7] rounded-[8px] w-full overflow-x-auto"
      style={{
        fontFamily: "Inter, sans-serif",
        fontFeatureSettings: "'ss01' on, 'cv01' on",
      }}
    >
      <table className="w-full text-left text-xs leading-5">
        <thead>
          <tr className="text-[#6D6D6D]">
            <th className="font-normal px-3 py-2.5 w-10">#</th>
            <th className="font-normal px-3 py-2.5">Application No. (TM No.)</th>
            <th className="font-normal px-3 py-2.5">Mark</th>
            <th className="font-normal px-3 py-2.5 w-20">Class</th>
            <th className="font-normal px-3 py-2.5">Type of Mark</th>
            <th className="font-normal px-3 py-2.5 whitespace-nowrap">
              Date of Filing
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr
              key={row.applicationId}
              className="border-t border-[#EEEEEE] text-[#222222] align-top"
            >
              <td className="px-3 py-2.5 text-[#6D6D6D]">{row.serial}</td>
              <td className="px-3 py-2.5 break-words">{row.tmNo || "—"}</td>
              <td className="px-3 py-2.5 break-words">{row.mark || "—"}</td>
              <td className="px-3 py-2.5">
                {row.classNumber != null ? row.classNumber : "—"}
              </td>
              <td className="px-3 py-2.5 break-words">
                {formatTypeOfMarkLabel(row.typeOfMark)}
              </td>
              <td className="px-3 py-2.5 whitespace-nowrap">
                {formatReadOnlyDate(row.dateOfFiling) || "—"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

type LegalCertificatePrepareKitViewProps = {
  leadId?: string | null;
  onSuccess?: (leadId: string) => void;
};

export default function LegalCertificatePrepareKitView({
  leadId,
  onSuccess,
}: LegalCertificatePrepareKitViewProps) {
  const [context, setContext] =
    useState<LegalCertificateCoveringNoteContext | null>(null);
  const [validityDate, setValidityDate] = useState<Date | null>(null);
  const [dateOfNote, setDateOfNote] = useState<Date | null>(null);
  const [kitItems, setKitItems] = useState<Record<KitItemKey, boolean>>({
    coveringNote: false,
    originalLegalCertificate: false,
    registrationCertificate: false,
    framedCertificate: false,
  });

  const [loading, setLoading] = useState<boolean>(!!leadId);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [previewPdfUrl, setPreviewPdfUrl] = useState<string | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [numPages, setNumPages] = useState<number | null>(null);

  const autoSaveDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );

  const applyContext = (data: LegalCertificateCoveringNoteContext) => {
    setContext(data);
    setValidityDate(
      data.validityDateOfRegistration
        ? (parseFilingApiDateString(data.validityDateOfRegistration) ?? null)
        : null
    );
    setDateOfNote(
      data.dateOfNoteWithLegalCertificate
        ? (parseFilingApiDateString(data.dateOfNoteWithLegalCertificate) ?? null)
        : null
    );
  };

  useEffect(() => {
    if (!leadId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    getLegalCertificateCoveringNote(leadId)
      .then((data) => {
        if (cancelled) return;
        applyContext(data);
      })
      .catch((err) => {
        if (!cancelled) {
          toast.error(
            err instanceof Error
              ? err.message
              : "Failed to load Prepare Kit data"
          );
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [leadId]);

  useEffect(() => {
    return () => {
      if (autoSaveDebounceRef.current)
        clearTimeout(autoSaveDebounceRef.current);
    };
  }, []);

  const buildPayload = () => ({
    dateOfNoteWithLegalCertificate: dateOfNote ? toLocalYmd(dateOfNote) : null,
    validityDateOfRegistration: validityDate ? toLocalYmd(validityDate) : null,
  });

  // Auto-save preview-relevant fields so the Covering Note PDF stays in sync.
  useEffect(() => {
    if (!leadId || loading || !context) return;
    if (autoSaveDebounceRef.current) clearTimeout(autoSaveDebounceRef.current);
    autoSaveDebounceRef.current = setTimeout(() => {
      saveLegalCertificateCoveringNote(leadId, buildPayload())
        .then((data) => {
          setContext(data);
        })
        .catch(() => {
          // Auto-save errors are non-blocking; explicit submit surfaces real issues.
        });
    }, 600);
    return () => {
      if (autoSaveDebounceRef.current)
        clearTimeout(autoSaveDebounceRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leadId, validityDate, dateOfNote]);

  const allKitItemsChecked = useMemo(
    () => KIT_ITEMS.every((item) => kitItems[item.key]),
    [kitItems]
  );

  const toggleKitItem = (key: KitItemKey) => {
    setKitItems((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const toggleSelectAllKitItems = () => {
    const next = !allKitItemsChecked;
    setKitItems({
      coveringNote: next,
      originalLegalCertificate: next,
      registrationCertificate: next,
      framedCertificate: next,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId || saving) return;
    if (!context) {
      toast.error("Prepare Kit data is not ready yet.");
      return;
    }
    if (!dateOfNote) {
      toast.error("Please enter Date of Note with Legal Certificate.");
      return;
    }
    if (autoSaveDebounceRef.current) {
      clearTimeout(autoSaveDebounceRef.current);
      autoSaveDebounceRef.current = null;
    }
    setSaving(true);
    try {
      const data = await saveLegalCertificateCoveringNote(
        leadId,
        buildPayload()
      );
      applyContext(data);
      toast.success("Prepare Kit details saved successfully.");
      onSuccess?.(leadId);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const handleGeneratePdf = async () => {
    if (!leadId) return;
    if (!dateOfNote) {
      toast.error(
        "Please enter Date of Note with Legal Certificate before generating PDF."
      );
      return;
    }
    setGenerating(true);
    try {
      await saveLegalCertificateCoveringNote(leadId, buildPayload());
      await generateLegalCertificateCoveringNotePdf(leadId);
      const fresh = await getLegalCertificateCoveringNote(leadId);
      setContext(fresh);
      toast.success("Covering Note PDF generated successfully.");
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to generate PDF"
      );
    } finally {
      setGenerating(false);
    }
  };

  const handleDownload = async (): Promise<void> => {
    const pdf = context?.pdf;
    if (!pdf?.url) {
      toast.error("Generate PDF first before downloading.");
      return;
    }
    setDownloadingPdf(true);
    try {
      const result = await downloadFileFromUrlWithFallback(
        pdf.url,
        pdf.filename ?? "covering-note.pdf"
      );
      if (result === "local") {
        toast.success("PDF saved to your device.");
      } else {
        toast.info(
          "Opened the PDF in a new tab. Use Save or Save as to store it locally."
        );
      }
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to download PDF"
      );
    } finally {
      setDownloadingPdf(false);
    }
  };

  const handlePreview = async () => {
    if (!leadId) return;
    if (!dateOfNote) {
      toast.error(
        "Please enter Date of Note with Legal Certificate before preview."
      );
      return;
    }
    setPreviewPdfUrl(null);
    setNumPages(null);
    if (context?.pdf?.url && !context.pdfIsStale) {
      setPreviewPdfUrl(context.pdf.url);
      return;
    }
    setPreviewing(true);
    try {
      await saveLegalCertificateCoveringNote(leadId, buildPayload());
      await generateLegalCertificateCoveringNotePdf(leadId);
      const fresh = await getLegalCertificateCoveringNote(leadId);
      setContext(fresh);
      if (fresh.pdf?.url) {
        setPreviewPdfUrl(fresh.pdf.url);
      } else {
        toast.error("Failed to generate PDF for preview.");
      }
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to generate preview"
      );
    } finally {
      setPreviewing(false);
    }
  };

  if (!leadId) {
    return (
      <div
        className="flex flex-col gap-[22px] w-full max-w-[1085px] justify-center py-12"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        <p className="text-sm text-[#6D6D6D]">
          Please complete the previous steps (select or create a lead) to access
          this form.
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div
        className="flex flex-col gap-[22px] w-full max-w-[1085px] justify-center py-12"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        <p className="text-sm text-[#6D6D6D]">Loading Prepare Kit data...</p>
      </div>
    );
  }

  if (!context) {
    return (
      <div
        className="flex flex-col gap-[22px] w-full max-w-[1085px] justify-center py-12"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        <p className="text-sm text-[#6D6D6D]">
          No data found. Please complete the previous Legal Certificate steps
          first.
        </p>
      </div>
    );
  }

  return (
    <form
      id={LEGAL_CERTIFICATE_PREPARE_KIT_FORM_ID}
      onSubmit={handleSubmit}
      className="flex flex-col gap-4 md:gap-5 w-full max-w-[1085px]"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      {/* Kit checklist */}
      <div className="flex flex-col gap-3 self-stretch">
        <div className="flex flex-row items-center justify-between gap-5 self-stretch">
          <span
            className="text-sm font-medium leading-5 text-[#222222]"
            style={LABEL_FEATURES}
          >
            The kit will contain the following
          </span>
          <button
            type="button"
            onClick={toggleSelectAllKitItems}
            className="text-xs leading-5 text-[#0070F0] hover:underline"
          >
            {allKitItemsChecked ? "Unselect All" : "Select All"}
          </button>
        </div>

        <div className="flex flex-col gap-2 self-stretch">
          {KIT_ITEMS.map((item) => {
            const checked = kitItems[item.key];
            return (
              <label
                key={item.key}
                className="flex flex-row items-center gap-2 self-stretch px-2.5 py-2.5 cursor-pointer min-h-11 bg-white border border-[#EEEEEE] rounded-[6px]"
              >
                <CheckboxBox checked={checked} />
                <input
                  type="checkbox"
                  className="sr-only"
                  checked={checked}
                  onChange={() => toggleKitItem(item.key)}
                />
                <span className="text-xs leading-5 text-[#222222]">
                  {item.label}
                </span>
              </label>
            );
          })}
        </div>
      </div>

      {/* Fetched details (from previous step) */}
      <h4 className="text-xs font-medium leading-5 text-[#6D6D6D]">
        Application Details (from previous step)
      </h4>

      {context.applications.length > 1 ? (
        <>
          <FetchedDetailsPanel
            rows={[
              { label: "Addressee", value: context.addressee ?? "" },
              { label: "Form of Address", value: context.formOfAddress ?? "" },
              {
                label: "No of Applications",
                value: String(
                  context.numberOfApplications ?? context.applications.length
                ),
              },
            ]}
          />
          <h4 className="text-xs font-medium leading-5 text-[#6D6D6D]">
            Applications in this kit
          </h4>
          <ApplicationsTable rows={context.applications} />
        </>
      ) : (
        <FetchedDetailsPanel
          rows={[
            { label: "Addressee", value: context.addressee ?? "" },
            { label: "Form of Address", value: context.formOfAddress ?? "" },
            {
              label: "Application Number (TM No.)",
              value:
                context.applications[0]?.tmNo ?? context.applicationNumber ?? "",
            },
            {
              label: "Date of Filing",
              value: formatReadOnlyDate(
                context.applications[0]?.dateOfFiling ?? context.dateOfFiling
              ),
            },
            {
              label: "Mark",
              value: context.applications[0]?.mark ?? context.mark ?? "",
            },
            {
              label: "Selected Class",
              value:
                context.applications[0]?.classNumber != null
                  ? String(context.applications[0].classNumber)
                  : context.selectedClass != null
                    ? String(context.selectedClass)
                    : "",
            },
            {
              label: "Selected Type of Mark",
              value: formatTypeOfMarkLabel(
                context.applications[0]?.typeOfMark ?? context.selectedTypeOfMark
              ),
            },
            {
              label: "No of Applications",
              value:
                context.numberOfApplications != null
                  ? String(context.numberOfApplications)
                  : "",
            },
          ]}
        />
      )}

      {/* Row 4: Validity date of Registration | Date of Note with Legal Certificate */}
      <div className="flex flex-col md:flex-row md:items-start w-full gap-4 md:gap-5">
        <div className="flex flex-1 min-w-0 flex-col w-full gap-1">
          <label
            className={LABEL_STYLE}
            style={{ ...LABEL_FEATURES, color: "#222222" }}
          >
            Validity date of Registration
          </label>
          <DateTimePicker
            value={validityDate}
            onChange={setValidityDate}
            showTime={false}
            buttonClassName="min-h-[44px] md:min-h-[36px] h-9 rounded-[6px] text-[12px] md:text-xs"
          />
        </div>
        <div className="flex flex-1 min-w-0 flex-col w-full gap-1">
          <label
            className={LABEL_STYLE}
            style={{ ...LABEL_FEATURES, color: "#222222" }}
          >
            Date of Note with Legal Certificate
          </label>
          <DateTimePicker
            value={dateOfNote}
            onChange={setDateOfNote}
            showTime={false}
            buttonClassName="min-h-[44px] md:min-h-[36px] h-9 rounded-[6px] text-[12px] md:text-xs"
          />
        </div>
      </div>

      {/* Generate PDF + Download + Preview */}
      <div className="flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center gap-2.5 pt-1">
        <button
          type="button"
          onClick={handleGeneratePdf}
          disabled={generating}
          className="flex min-h-[44px] sm:h-9 items-center justify-center gap-2 rounded-[6px] border border-[#EEEEEE] bg-white px-4 py-2.5 sm:py-2 text-[12px] md:text-xs font-medium leading-5 text-[#222222] hover:bg-[#F7F7F7] disabled:opacity-60 disabled:cursor-not-allowed touch-manipulation"
          style={{ fontFamily: "Inter, sans-serif" }}
        >
          <img src={pdfIcon} alt="" className="h-[22px] w-[22px] shrink-0" />
          {generating ? "Generating..." : "Generate PDF"}
        </button>
        <button
          type="button"
          onClick={() => {
            void handleDownload();
          }}
          disabled={downloadingPdf}
          className="min-h-[44px] sm:h-9 rounded-[6px] bg-[#222222] px-4 py-2.5 sm:py-2 text-center text-[12px] md:text-xs font-medium leading-5 text-white hover:bg-[#333333] touch-manipulation disabled:opacity-60 disabled:cursor-not-allowed"
          style={{ fontFamily: "Inter, sans-serif" }}
        >
          {downloadingPdf ? "Downloading…" : "Download"}
        </button>
        <button
          type="button"
          onClick={handlePreview}
          disabled={previewing}
          className="min-h-[44px] sm:h-9 rounded-[6px] border border-[#EEEEEE] bg-white px-4 py-2.5 sm:py-2 text-center text-[12px] md:text-xs font-medium leading-5 text-[#222222] hover:bg-[#F7F7F7] disabled:opacity-60 touch-manipulation"
          style={{ fontFamily: "Inter, sans-serif" }}
        >
          {previewing ? "Generating..." : "Preview PDF"}
        </button>
      </div>

      {/* PDF Preview modal */}
      {previewPdfUrl && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-2 sm:p-4">
          <div className="relative flex h-[95vh] sm:h-[90vh] w-full max-w-4xl flex-col rounded-lg bg-white shadow-xl overflow-hidden">
            <div className="flex shrink-0 items-center justify-between border-b border-[#e3e3e3] px-3 sm:px-4 py-3">
              <span
                className="text-xs sm:text-sm font-medium text-[#222] truncate pr-2"
                style={{ fontFamily: "Inter, sans-serif" }}
              >
                Covering Note PDF Preview
              </span>
              <button
                type="button"
                onClick={() => setPreviewPdfUrl(null)}
                className="flex size-10 sm:size-8 items-center justify-center rounded text-[#6d6d6d] hover:bg-[#eee] hover:text-[#222] touch-manipulation shrink-0"
                aria-label="Close"
              >
                <HiOutlineX className="size-5" strokeWidth={2} />
              </button>
            </div>
            <div className="min-h-0 flex-1 overflow-auto p-2 sm:p-4">
              <Document
                file={previewPdfUrl}
                onLoadSuccess={({ numPages: n }) => setNumPages(n)}
                loading={
                  <p
                    className="text-sm text-[#6d6d6d]"
                    style={{ fontFamily: "Inter, sans-serif" }}
                  >
                    Loading PDF…
                  </p>
                }
                error={
                  <p
                    className="text-sm text-red-600"
                    style={{ fontFamily: "Inter, sans-serif" }}
                  >
                    Failed to load PDF.
                  </p>
                }
                className="flex flex-col items-center gap-4"
              >
                {numPages != null &&
                  Array.from({ length: numPages }, (_, i) => (
                    <Page
                      key={i + 1}
                      pageNumber={i + 1}
                      width={Math.min(
                        800,
                        typeof window !== "undefined"
                          ? window.innerWidth - 24
                          : 800
                      )}
                      renderTextLayer
                      renderAnnotationLayer
                    />
                  ))}
              </Document>
            </div>
          </div>
        </div>
      )}
    </form>
  );
}
